import type { ContainerViewTemplateName } from 'libs/shared/src/template/app/components/container-gallery/models/view-template-settings';
import { ArticleGroupType } from '../../../articles/article';
import { Color, HEXColor, PictureDisplayMode, ResponsiveAttribute } from '../../../basic';
import { ISides } from '../../../configs/config';
import { ModelType } from '../../../search/search-base-item';
import { IMethod } from '../basic';
import {
  GsStructBorderRadius,
  GsStructBorderStyle,
  GsStructBorderWidth,
  GsStructComponent,
  SharedItemOverlayDisplayMode,
  GsStructPageItems,
  GsStructSwiper,
  GsStructTextAlignment,
  GsStructVerticalAlignment
} from '../component';
import { IPaginated } from './../basic/paginated';
import { IStandardItem } from './../basic/standard-item';
import { ITeaserBadgeSettings } from './teaser-badge-settings';

export interface GsStructContainerGalleryComponent
  extends GsStructComponent<IContainerGalleryRender>,
    GsStructPageItems<ContainerGallerySortOrder>,
    GsStructSwiper,
    Partial<ITeaserBadgeSettings> {
  /** The specified type-key for this struct */
  componentType: 'ContainerGallery';
  /** The id/s of the container/s to display */
  iDs: string;
  /** The id of the timeline to filter by */
  timelineID?: string;
  /** Number of items to skip */
  skip?: number;
  /** Should only valid (from/to) items be displayed? */
  onlyValid?: boolean;
  /** The view-template to use for rendering the items */
  viewTemplate?: ContainerViewTemplateName;
  // Image/Box Settings
  /** The height of the gallery in px */
  galleryHeight?: ResponsiveAttribute<number>;
  /** Enable overlays for images inside the view-template? */
  imageOverlay?: boolean;
  /** The transparency of the image overlay inside the view-template (0-100) */
  imageOverlayTransparency?: number;
  /** The color of the image overlay inside the view-template */
  imageOverlayColor?: Color;
  // Text Settings
  /** Text color for the title and subtitle in the view-template */
  textColor?: Color;
  /** Text shadow enabled inside the view-template? */
  textShadow?: boolean;
  /** Text background enabled inside the view-template? */
  textBackground?: boolean;
  /** Text background color for the text in the view-template */
  textBackgroundColor?: Color;
  /** Text alignment inside the items */
  itemTextAlignment?: GsStructTextAlignment;
  /** Vertical alignment of the text block inside the items (box view template) */
  itemVerticalAlignment?: GsStructVerticalAlignment;
  /** Whether to show the item subtitle (second text line below the title) */
  showItemSubtitle?: boolean;
  /** Shorten item titles inside the slider */
  shortenItemTitles?: boolean;
  // Slider Settings
  /** Navigation type for the slider */
  sliderNavigationType?: SliderNavigationType;
  // Item / Image Settings
  /** Should photos be shown */
  showPhotos?: boolean;
  /** Padding around each item (responsive; legacy {@link ISides} values are still supported) */
  itemPadding?: ResponsiveAttribute<ISides> | ISides;
  /** Height of the item image */
  itemImageHeight?: ResponsiveAttribute<number>;
  /** Crop the item images to a 1:1 square instead of using {@link itemImageHeight} */
  forceSquareImage?: boolean;
  /** Border color */
  borderColor?: Color;
  /** Border width  */
  borderWidth?: GsStructBorderWidth;
  /** Border style  */
  borderStyle?: GsStructBorderStyle;
  /** Border radius */
  borderRadius?: GsStructBorderRadius;
  /** Show new badge for the last X days */
  newForDays?: number;
  /** Whether article tiles show the quick-buy shopping cart button on hover */
  showShoppingCartButton?: boolean;
  /** Show button below items */
  showItemButton?: boolean;
  /** Custom text for the item button */
  itemButtonText?: string;
  /** Button-Text fest nach Warengruppe („Zur Pflanze“/„Zum Artikel“) statt itemButtonText (CS4-2680) */
  itemButtonTextByArticleGroup?: boolean;
  /** Steuert, wann das dekorative Overlay-Bild auf den Items angezeigt wird */
  itemOverlayDisplayMode?: SharedItemOverlayDisplayMode;
  /** Steuert den Bildmodus der Item-Bilder (Cut=cover, Full=contain, Auto=vom Foto) */
  pictureDisplayMode?: PictureDisplayMode;
  // Hover Settings
  /** Zoom the item image on hover */
  itemZoomOnHover?: boolean;
  /** Hover text to display on the item image (if empty, no text shown) */
  itemHoverText?: string;
  /** Text color for the hover overlay */
  itemHoverTextColor?: Color;
  /** Show a background color overlay on hover */
  itemShowHoverBackground?: boolean;
  /** Background color for the hover overlay */
  itemHoverBackgroundColor?: Color;
}

/** Possible sorting orders for the container gallery items */
export enum ContainerGallerySortOrder {
  Title = 0,
  Order = 1,
  Random = 2
}

export enum SliderNavigationType {
  Bullet = 0,
  Scrollbar = 1,
  None = 2
}

export interface IContainerGalleryRender {
  items: IPaginated<IContainerItem>;
  methods?: {
    populate?: IMethod;
    /** Loads the full article-info render data for an article, used by the quick-buy dialog on article tiles */
    articleInfos?: IMethod;
  };
}

/**
 * The items inside the container
 * @summary Can include any type of item "Article", "Category", "Picture", "Video", "Page", "Report", "TeamMember" etc.
 * @summary Each container placed in the page has an extra field "viewTemplate" to define how the items should be rendered/displayed
 * @summary A viewTemplate can only support certain item types. E.g. a "ArticleList" viewTemplate would only support items of type "Article".
 * @summary The mapping of which viewTemplate supports which item types is done inside the rendering component.
 */
export interface IContainerItem extends IStandardItem<'ContainerItem', ModelType> {
  /** The key for the linkTarget to redirect to on click */
  linkTargetKey?: string;
  /** The id of the linkTarget to redirect to on click */
  linkTargetId?: number;
  /** Array of categories wich are assigned to this item */
  categoryIds?: number[];
  /** Color for an possible teaser of this item */
  teaserColor?: HEXColor;
  /** A rating assigned to this item */
  rating?: number;
  /** The amount of given ratings to this item */
  ratingCount?: number;
  /** Stock quantity of the item if applicable */
  stockQuantity?: number;
  /** The color associated with this item */
  color?: HEXColor;
  /** Ids of assigned article groups */
  articleGroupNumbers?: string[];
  /** Article group type of the linked article, e.g. to tell tickets and vouchers apart */
  articleGroupType?: ArticleGroupType;
  /** The primary key of this container item */
  key?: string;
  /**
   * Description maintained manually on the container item (CS4-2284) — unlike `text`, which can also
   * carry the linked entity's own description from the search index.
   */
  manualDescription?: string;
}
